import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { Calendar, User, ArrowLeft } from 'lucide-react';

const BlogPost = () => {
  const { id } = useParams();

  // Blog posts data (same as in Blog.jsx)
  const blogPosts = {
    1: {
      id: 1,
      title: "From Farm to Global Markets: Our Visit to a GAP-Certified Grape Farm",
      image: "https://customer-assets.emergentagent.com/job_merchant-cornerstone/artifacts/ldomet8t_7.png",
      date: "March 31, 2025",
      author: "Cornerstone Team",
      category: "Farm Visit"
    },
    2: {
      id: 2,
      title: "Visit to a GAP Certified Pomegranate Farm – A Learning Experience",
      image: "https://customer-assets.emergentagent.com/job_merchant-cornerstone/artifacts/k0fezkkb_10.png",
      date: "March 28, 2025",
      author: "Cornerstone Team",
      category: "Farm Visit"
    }
  };

  const post = blogPosts[id];

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">Blog Post Not Found</h1>
          <Link to="/blog" className="text-emerald-600 hover:text-emerald-700">
            ← Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Blog Post Content */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link 
            to="/blog"
            className="inline-flex items-center space-x-2 text-emerald-700 hover:text-emerald-800 mb-8 font-semibold"
          >
            <ArrowLeft size={20} />
            <span>Back to Blog</span>
          </Link>

          {/* Category Badge */}
          <div className="mb-6">
            <span className="bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
              {post.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Meta Info */}
          <div className="flex items-center space-x-6 text-gray-600 mb-8 pb-8 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <Calendar size={20} />
              <span>{post.date}</span>
            </div>
            <div className="flex items-center space-x-2">
              <User size={20} />
              <span>{post.author}</span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-auto"
            />
          </div>

          {/* Blog Content */}
          <div className="prose prose-lg max-w-none">
            {post.id === 1 && (
              // Grape Farm Content
              <div>
                <p className="text-xl text-gray-700 leading-relaxed mb-8">
                  At Cornerstone Exim, we believe that quality begins at the source. Last month, our team had the opportunity to visit a GAP-certified grape farm, gaining firsthand insight into the cultivation practices that ensure premium-quality grapes for international markets.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">A Walk Through the Vineyard</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Walking through the vineyard rows was an experience in itself. The grapevines were carefully aligned, forming long, shaded pathways with lush green canopies overhead. Each plant was meticulously maintained, ensuring optimal sunlight, airflow, and growth conditions.
                </p>

                <div className="my-8 rounded-xl overflow-hidden shadow-lg">
                  <img
                    src="https://customer-assets.emergentagent.com/job_merchant-cornerstone/artifacts/71ye5gzk_8.png"
                    alt="Vineyard pathway with green canopy"
                    className="w-full h-auto"
                  />
                  <p className="text-center text-sm text-gray-600 mt-2 italic">Vineyard pathways with lush green canopy</p>
                </div>

                <p className="text-gray-700 leading-relaxed mb-6">
                  The grapes themselves were a sight to behold—plump, evenly colored, and naturally ripened under controlled conditions. The vibrant red-purple clusters reflected not just ripeness, but also the dedication and precision that goes into producing export-quality fruit.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What GAP Certification Means</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The farm we visited follows Good Agricultural Practices (GAP), a globally recognized standard that ensures:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                  <li>Food safety and hygiene</li>
                  <li>Sustainable farming methods</li>
                  <li>Responsible use of pesticides and fertilizers</li>
                  <li>Worker health and safety</li>
                  <li>Traceability from farm to consumer</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This certification is critical for export markets, as it guarantees that the produce meets stringent international quality and safety standards.
                </p>

                <div className="my-8 rounded-xl overflow-hidden shadow-lg">
                  <img
                    src="https://customer-assets.emergentagent.com/job_merchant-cornerstone/artifacts/8koy8c7s_9.png"
                    alt="GAP-certified grapes hanging on vine"
                    className="w-full h-auto"
                  />
                  <p className="text-center text-sm text-gray-600 mt-2 italic">Premium quality grapes on the vine</p>
                </div>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Precision Farming for Premium Quality</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  One of the key highlights of the visit was understanding how technology and traditional expertise come together:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                  <li>Controlled irrigation systems ensure the right amount of water at every stage</li>
                  <li>Canopy management improves grape quality and prevents disease</li>
                  <li>Soil health monitoring enhances nutrient balance</li>
                  <li>Careful harvesting techniques preserve freshness and shelf life</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Each bunch of grapes is handled with care, ensuring it meets export-grade specifications.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Why It Matters for Global Trade</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For an export-import business like Cornerstone Exim, sourcing from GAP-certified farms is essential. It allows us to:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                  <li>Deliver consistent quality to international buyers</li>
                  <li>Meet compliance requirements of global markets</li>
                  <li>Build long-term trust with clients</li>
                  <li>Support sustainable and ethical farming practices</li>
                </ul>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Our Commitment</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Our visit reaffirmed our commitment to sourcing only the best. By working closely with certified farms, we ensure that every shipment reflects quality, safety, and reliability.
                </p>
                <p className="text-xl font-semibold text-emerald-800 leading-relaxed">
                  At Cornerstone Exim, we don't just trade products—we build connections between farms and global markets.
                </p>
              </div>
            )}

            {post.id === 2 && (
              // Pomegranate Farm Content
              <div>
                <p className="text-xl text-gray-700 leading-relaxed mb-8">
                  Last month, we had the opportunity to visit a GlobalG.A.P. certified pomegranate farm in India as part of our preparation for the upcoming export season.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Although it was not the harvest period, the farm was in the flowering stage, offering a valuable insight into the early phase of crop development.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Farm Condition & Management</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The farm was very well maintained, reflecting strong adherence to global farming standards. Clean surroundings, proper spacing between plants, and organized irrigation systems showed the farmer's commitment to quality production.
                </p>

                <div className="my-8 rounded-xl overflow-hidden shadow-lg">
                  <img
                    src="https://customer-assets.emergentagent.com/job_merchant-cornerstone/artifacts/0f6wr5g2_11.png"
                    alt="GAP-certified pomegranate farm with red fruits"
                    className="w-full h-auto"
                  />
                  <p className="text-center text-sm text-gray-600 mt-2 italic">Well-maintained pomegranate farm with proper spacing and irrigation</p>
                </div>

                <p className="text-gray-700 leading-relaxed mb-4">
                  We observed:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                  <li>Healthy plants with uniform flowering</li>
                  <li>Proper drip irrigation system for efficient water use</li>
                  <li>Good pest and disease control practices</li>
                  <li>Clean and hygienic farm environment</li>
                </ul>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Importance of GAP Certification</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  A GlobalG.A.P. certified farm ensures:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                  <li>Safe and traceable produce</li>
                  <li>Controlled use of fertilizers and pesticides</li>
                  <li>Compliance with international export standards</li>
                  <li>Sustainable farming practices</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This is especially important for markets like Europe, Middle East, and other premium destinations.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Learning from the Visit</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Even though fruits were not yet developed, the flowering stage helped us understand:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                  <li>Crop health and future yield potential</li>
                  <li>Flower-to-fruit conversion process</li>
                  <li>Importance of early-stage care for quality produce</li>
                </ul>

                <p className="text-gray-700 leading-relaxed mb-4">
                  We also interacted with farmers and gained practical knowledge about:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                  <li>Seasonal planning</li>
                  <li>Export quality requirements</li>
                  <li>Farm-level challenges and solutions</li>
                </ul>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Conclusion</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This visit strengthened our confidence in sourcing high-quality pomegranates for export. A well-managed farm at the flowering stage is a strong indicator of a good harvest ahead.
                </p>
                <p className="text-xl font-semibold text-emerald-800 leading-relaxed">
                  We look forward to the upcoming season with better understanding and stronger partnerships with certified farms.
                </p>
              </div>
            )}
          </div>

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Interested in Our Export-Quality Products?
            </h3>
            <p className="text-gray-700 mb-6">
              Contact us to learn more about our GAP-certified produce and export services.
            </p>
            <Link
              to="/#contact"
              className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Get in Touch
            </Link>
          </div>

          {/* Back to Blog */}
          <div className="mt-12 text-center">
            <Link 
              to="/blog"
              className="inline-flex items-center space-x-2 text-emerald-700 hover:text-emerald-800 font-semibold"
            >
              <ArrowLeft size={20} />
              <span>Back to All Articles</span>
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost;
